/**
 * Structured Handoff renderer for the Inspector (Phase 1).
 *
 * Renders an already-composed AutomaticHandoff clearly — no network, no
 * invention. Empty / missing fields stay omitted or marked unknown rather
 * than guessed.
 */

import type { AutomaticHandoff, ComposedTest, HandoffReference } from '@brunel/shared';

export function HandoffView({ handoff }: { readonly handoff: AutomaticHandoff | null }) {
  if (handoff === null) {
    return (
      <div className="inspector-empty" role="status">
        <p>No handoff selected.</p>
        <p className="muted">Expand a Worker or pin a finished task to see its structured handoff here.</p>
      </div>
    );
  }

  return (
    <article className="handoff-view stack" aria-label="Structured handoff">
      <header className="handoff-headline">
        <span className={`handoff-status status-${handoff.status}`}>{handoff.status}</span>
        <h3>{handoff.headline}</h3>
      </header>

      <dl className="handoff-fields stack">
      <HandoffField label="Worker" value={handoff.worker?.label ?? null} />
      <HandoffField label="Project" value={handoff.project} />
      <HandoffField label="Task" value={handoff.task?.title ?? handoff.task?.id ?? null} />
      <HandoffField label="Goal" value={handoff.goal} />
      <HandoffField label="Result" value={handoff.result} />
      <HandoffField label="Runtime" value={handoff.runtime} />
      <HandoffField label="Model" value={handoff.model} />
      <HandoffField label="Started" value={handoff.startedAt} />
      <HandoffField label="Ended" value={handoff.endedAt} />
      <HandoffField label="Session" value={handoff.sessionLifecycle} />

      <HandoffList
        label="Files changed"
        items={handoff.filesChanged}
        unknown="unknown"
        empty="none reported"
      />
      <HandoffTests tests={handoff.tests} />
      <HandoffReferences references={handoff.codeReferences} />
      <HandoffList
        label="Blockers"
        items={handoff.blockers}
        unknown="unknown"
        empty="none reported"
      />
      <HandoffList label="Remaining work" items={handoff.remainingWork} empty="none" />
      <HandoffList label="Owner attention" items={handoff.ownerAttention} empty="none" />
      <HandoffField label="Next step" value={handoff.recommendedNextStep} />
      <HandoffList label="Missing facts" items={handoff.factsMissing} empty="none" />
      </dl>
    </article>
  );
}

function HandoffField({ label, value }: { readonly label: string; readonly value: string | null }) {
  if (value == null || value === '') return null;
  return (
    <div className="handoff-field">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}


function HandoffList({
  label,
  items,
  unknown,
  empty,
}: {
  readonly label: string;
  readonly items: readonly string[] | null;
  readonly unknown?: string;
  readonly empty: string;
}) {
  if (items == null) {
    if (unknown == null) return null;
    return (
      <div className="handoff-field">
        <dt>{label}</dt>
        <dd className="muted">{unknown}</dd>
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="handoff-field">
        <dt>{label}</dt>
        <dd className="muted">{empty}</dd>
      </div>
    );
  }
  return (
    <div className="handoff-field">
      <dt>{label}</dt>
      <dd>
        <ul className="handoff-list">
          {items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </dd>
    </div>
  );
}

function HandoffTests({ tests }: { readonly tests: readonly ComposedTest[] | null }) {
  if (tests == null) {
    return (
      <div className="handoff-field">
        <dt>Tests</dt>
        <dd className="muted">unknown</dd>
      </div>
    );
  }
  if (tests.length === 0) {
    return (
      <div className="handoff-field">
        <dt>Tests</dt>
        <dd className="muted">none reported</dd>
      </div>
    );
  }
  return (
    <div className="handoff-field">
      <dt>Tests</dt>
      <dd>
        <ul className="handoff-list">
          {tests.map((test, index) => {
            const name = test.name ?? test.command ?? `Test ${index + 1}`;
            return (
              <li key={`${name}-${test.status}-${index}`}>
                <span className={`test-status status-${test.status}`}>{test.status}</span>
                {' '}
                {name}
                {test.detail != null && test.detail !== '' ? (
                  <span className="muted"> — {test.detail}</span>
                ) : null}
              </li>
            );
          })}
        </ul>
      </dd>
    </div>
  );
}

function HandoffReferences({ references }: { readonly references: readonly HandoffReference[] }) {
  if (references.length === 0) return null;
  return (
    <div className="handoff-field">
      <dt>References</dt>
      <dd>
        <ul className="handoff-list">
          {references.map((ref) => (
            <li key={`${ref.kind}-${ref.reference}`}>
              <span className="tag">{ref.kind}</span>
              {' '}
              {ref.url != null ? (
                <a href={ref.url} target="_blank" rel="noreferrer noopener">{ref.label}</a>
              ) : (
                <span>{ref.label}</span>
              )}
              {ref.reference !== ref.label && (
                <span className="muted"> ({ref.reference})</span>
              )}
            </li>
          ))}
        </ul>
      </dd>
    </div>
  );
}
